// src/components/Register.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import API from "../../util/api";
import { userLogin } from "../../sagas/actions";
import { showErrorModal } from "../../util/general";

import CountryMembership from "./CountryMembership";
import PersonalInformation from "./PersonalInformation";
import ContactTypeStep    from "./ContactTypeStep";
import OrgInfoStep        from "./OrgInfoStep";

const Outer = styled.div`
  border-radius: 5px;
  overflow: hidden;
  width: 1000px;
  margin: 0 auto;
`;

const Form = styled.form`
  padding: 10px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const LinkButton = styled.a`
  color: #555;
  text-decoration: none;
  padding: 8px 12px;
  background: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
`;

const Button = styled.button`
  background: #195174;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  &:hover { background: #2980b9; }
  &[disabled] { background: #ccc; cursor: not-allowed; }
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
`;
const ModalContent = styled.div`
  background: white; padding: 24px; border-radius: 8px;
  text-align: center;
`;

export default function Register() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [data, setData] = useState({
    // ─── Step 1 defaults ───
    country: "",
    membership: "",

    // ─── Step 2 defaults ───
    title:            "",
    first_name:       "",
    last_name:        "",
    street:           "",
    street2:          "",
    city:             "",
    state:            "",
    zip:              "",
    phone:            "",
    ext:              "",
    fax:              "",
    email:            "",
    confirm_email:    "",
    password:         "",
    confirm_password: "",

    // ─── Step 3 default ───
    contact_type:     "",

    // ─── Steps 4–7 defaults ───
    org_state:     "",
    org_zip:       "",
    org_id:        "",                 
    org_name:      "",
    school_level:  "",
    iparent_board: false,
    news_letter:   true,
  });

  const isEducator = data.membership === "ed";
  const totalSteps = isEducator ? 7 : 4;

  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    // everything else stays generic:
    setData(d => ({
      ...d,
      [name]: type === "checkbox" && name !== "country"
        ? checked
        : value
    }));
  };

  const validateStep2 = () => {
    const errs = {};
    if (!data.first_name) errs.first_name = "Required";
    if (!data.last_name)  errs.last_name  = "Required";
    if (!data.street)     errs.street     = "Required";
    if (!data.city)       errs.city       = "Required";
    if (!data.state)      errs.state      = "Required";
    // if (!/^\d{5}(-\d{4})?$/.test(data.zip))
    //   errs.zip = "Invalid ZIP";
    if (!/^\d{10}$/.test(data.phone.replace(/\D/g, "")))
      errs.phone = "Invalid phone";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      errs.email = "Invalid email";
    if (data.email !== data.confirm_email)
      errs.confirm_email = "Emails must match";
    if (data.password.length < 8)
      errs.password = "Min 8 chars";
    if (data.password !== data.confirm_password)
      errs.confirm_password = "Must match";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const loginAdult = async () => {
    try {
      const resp = await API.userLogin(data.email, data.password);
      if (!resp.access_token) {
        showErrorModal("Login failed. Please check your password.");
        navigate("/login");
      } else {
        dispatch(userLogin(resp));
        navigate("/myinfo");
      }
    } catch (err) {
      console.error(err);
      showErrorModal("Login error. Please sign in manually.");
      navigate("/login");
    }
  };

    const onNext = () => {
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!validateStep2()) return;
      setStep(3);
      return;
    }
    if (step < totalSteps) {
      setStep(s => s + 1);
      return;
    }

    // final submit
    const payload = {
      country:          data.country,
      membership_code:  data.membership, 
      title:            data.title,
      first_name:       data.first_name,
      last_name:        data.last_name,
      address:          data.street + data.street2,
      city:             data.city,
      state:            data.state,
      zip:              Number(data.zip),
      phone:            Number(data.phone.replace(/\D/g, "")),
      ext:              data.ext || "",
      fax:              data.fax || "",
      email:            data.email,
      confirm_email:    data.confirm_email,
      password:         data.password,
      confirm_password: data.confirm_password,
      contact_type:     data.contact_type,
      org_state:        data.org_state,
      org_zip:          data.org_zip,
      org_id:           data.org_id,
      org_name:         data.org_name,
      iparent_board:    data.iparent_board,
      news_letter:      data.news_letter,
      school_level:     data.school_level,
    };

    API.registerUser(payload)
      .then(resp => {
        if (resp.status === "Success") {
          setShowSuccessModal(true);
          loginAdult();
        } else {
          showErrorModal(resp.message || "Registration failed");
          navigate("/login");
        }
      })
      .catch(err => {
        console.error(err);
        showErrorModal("Registration error, please try again.");
        navigate("/login");
      });
  };


  const onBack   = () => step > 1 && setStep(s => s - 1);
  const onCancel = () => navigate("/login");
  const toggleShowPwd = () => setShowPwd(v => !v);

let nextDisabled = false;
  if (step === 1) {
    nextDisabled = !(data.country && data.membership);
  } else if (step === 2) {
    nextDisabled = Object.keys(errors).length > 0;
  } else if (step === 3) {
    nextDisabled = !data.contact_type;
  } else if (step === 4) {
    nextDisabled = isEducator && !data.org_state;
  } else if (step === 5) {
    nextDisabled = isEducator && !data.org_zip;
  } else if (step === 6) {
    nextDisabled = isEducator && (!data.org_id && !data.org_name);
  } else if (!isEducator && step === 4) {
    nextDisabled = !data.org_name;
  }

  return (
    <Outer>
      <Form onSubmit={e => { e.preventDefault(); onNext(); }}>
        {step === 1 && (
          <CountryMembership data={data} onChange={handleChange} />
        )}
        {step === 2 && (
          <PersonalInformation
            data={data}
            onChange={handleChange}
            errors={errors}
            showPwd={showPwd}
            toggleShowPwd={toggleShowPwd}
          />
        )}
        {step === 3 && (
          <ContactTypeStep
            data={data}
            onChange={handleChange}
          />
        )}
        {step >= 4 && (
          <OrgInfoStep
            data={data}
            onChange={handleChange}
            step={step}
          />
        )}

        <Actions>
          <LinkButton onClick={onCancel}>&laquo; Cancel</LinkButton>
          {step > 1 && <LinkButton onClick={onBack}>&lt; Back</LinkButton>}
          <Button
            type="button"
            onClick={onNext}
            // disabled={step < totalSteps && nextDisabled}
          >
            {step < totalSteps ? "Next >" : "Finish"}
          </Button>
        </Actions>
      </Form>

      {showSuccessModal && (
        <ModalOverlay>
          <ModalContent>
            <h2>Registration Successful!</h2>
            <p>You are being logged in now…</p>
          </ModalContent>
        </ModalOverlay>
      )}
    </Outer>
  );
}
